import type { Feature, Point } from "geojson";

import { hasCoordinates, isSameCoord, validateGeometry } from "./geometryUtils";

/**
 * Update a coordinate in a feature.
 *
 * @param feature - The GeoJSON feature.
 * @param nestedIdx - A string representing the nested index path (e.g. "0.1.2") from metaCoordinateIdx.
 * @param updateValue - Either an absolute new coordinate, or a translation [deltaLng, deltaLat].
 * @param asTranslation - If true, the updateValue is applied as a translation; otherwise it replaces the coordinate.
 * @returns The updated feature.
 */
export default function (
  feature: Feature,
  nestedIdx: string,
  updateValue: [number, number],
  asTranslation: boolean = false,
): Feature {
  if (!hasCoordinates(feature.geometry)) {
    throw new Error("Geometry does not have coordinates property");
  }

  let updatedCoordinates: any;

  // Special handling for Point geometry which has flat coordinates
  if (feature.geometry.type === "Point") {
    const pointGeometry = feature.geometry as Point;
    if (nestedIdx === "0") {
      updatedCoordinates = asTranslation
        ? [
            pointGeometry.coordinates[0] + updateValue[0],
            pointGeometry.coordinates[1] + updateValue[1],
          ]
        : updateValue;
    } else {
      throw new Error("Invalid index for Point geometry");
    }
  } else {
    updatedCoordinates = (function update(coords: any, indices: number[]): any {
      // At the last index level, update the coordinate.
      if (indices.length === 1) {
        // If updating the first coordinate of a closed ring,
        // update both the first and the last coordinate.
        if (
          indices[0] === 0 &&
          coords.length > 1 &&
          isSameCoord(coords[0], coords[coords.length - 1])
        ) {
          return coords.map((coord: number[], i: number) =>
            i === 0 || i === coords.length - 1
              ? asTranslation
                ? [coord[0] + updateValue[0], coord[1] + updateValue[1]]
                : updateValue
              : coord,
          );
        }
        // Otherwise, update only the targeted coordinate.
        return coords.map((coord: number[], i: number) =>
          i === indices[0]
            ? asTranslation
              ? [coord[0] + updateValue[0], coord[1] + updateValue[1]]
              : updateValue
            : coord,
        );
      }
      // Recursively go deeper in the nested arrays.
      return coords.map((coord: any, i: number) =>
        i === indices[0] ? update(coord, indices.slice(1)) : coord,
      );
    })(feature.geometry.coordinates, nestedIdx.split(".").map(Number));
  }

  const updatedFeature = {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: updatedCoordinates,
    },
  };

  // Validate the result after modification
  validateGeometry(updatedFeature);

  return updatedFeature;
}
